/** @jsxRuntime classic */
/** @jsx jsx */
/* eslint-disable @atlaskit/ui-styling-standard/no-container-queries -- The named container and width queries are local to this banner so side panels can change its layout independently of the viewport. Verify reflow at 270–320px and 400% zoom. */
import React from 'react';
import { css, cssMap as unboundedCssMap } from '@compiled/react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import type ContainerAtRule from '@atlaskit/css/at-rules/container';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';
import AvatarGroup from '@atlaskit/avatar-group/avatar-group';
import type { AvatarGroupOverrides, AvatarProps } from '@atlaskit/avatar-group/types';
import Avatar from '@atlaskit/avatar/avatar';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading/heading';

import { expValEquals } from '@confluence/feature-experiments';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

import { ApprovalsProgressBar } from '../MultiColorProgressBar/ApprovalsProgressBar.tsx';

export type BannerAvatarProps = AvatarProps & { userId?: string };
export type ProfileCardMode = 'hover' | 'click';
type AvatarGroupItemRender = NonNullable<
	NonNullable<AvatarGroupOverrides['AvatarGroupItem']>['render']
>;
type AvatarGroupItemRenderParams = Parameters<AvatarGroupItemRender>;

// ADS cssMap does not support container declarations or queries, so use Compiled
// for this local layout and apply it to native elements rather than bounded xcss.
const styles = unboundedCssMap({
	container: {
		// Use the banner's width, not the window's width, to position the icon.
		containerName: 'approval-banner',
		containerType: 'inline-size',
		minWidth: '0px',
	},
	wrapper: {
		backgroundColor: token('elevation.surface.sunken'),
		padding: token('space.200'),
		borderRadius: token('radius.medium'),
		display: 'flex',
		// Move the controls below the text only when they no longer fit beside it.
		// There is no fixed width for moving the button; it depends on the content.
		flexWrap: 'wrap',
		minWidth: '0px',
		alignItems: 'center',
		gap: token('space.200'),
	},
	supportingContent: {
		display: 'flex',
		flex: '1 1 auto',
		// Let the progress bar, avatars and button use more lines when needed.
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: token('space.200'),
		minWidth: '0px',
		maxWidth: '100%',
	},
	messageSupportingContent: {
		// Keep the button at its normal width beside short messages.
		flexGrow: 0,
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gridColumn: '1',
		// Above 560px, keep the icon to the left of the heading and description.
		gridRow: '1 / span 2',
		justifySelf: 'start',
		padding: token('space.050'),
		['@container approval-banner (max-width: 560px)' satisfies ContainerAtRule]: { gridRow: '1' },
	},
	// Banner spec spacing (confluence_approvals_banner_updates_exp): the 16px icon sits flush
	// with the 16px banner padding, 12px from the text, with 2px between heading and description.
	iconWrapperSpec: {
		padding: token('space.0'),
	},
	leftSectionSpec: {
		columnGap: token('space.150'),
		rowGap: token('space.025'),
	},
	leftSection: {
		display: 'grid',
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		flex: '0 1 auto',
		// Give the text room before moving the controls below it.
		width: 'max-content',
		maxWidth: '100%',
		alignItems: 'center',
		columnGap: token('space.100'),
		rowGap: token('space.050'),
		minWidth: '0px',
		overflowWrap: 'anywhere',
		// At 560px or less, put the progress banner's icon above its text.
		['@container approval-banner (max-width: 560px)' satisfies ContainerAtRule]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
	messageSection: {
		flexGrow: 1,
		// For message banners, keep the icon beside the heading at 560px or less.
		['@container approval-banner (max-width: 560px)' satisfies ContainerAtRule]: {
			gridTemplateColumns: 'auto minmax(0, 1fr)',
		},
	},
	headingWrapper: {
		gridColumn: '2',
		gridRow: '1',
		minWidth: '0px',
		['@container approval-banner (max-width: 560px)' satisfies ContainerAtRule]: {
			gridColumn: '1',
			gridRow: '2',
		},
	},
	messageHeading: {
		['@container approval-banner (max-width: 560px)' satisfies ContainerAtRule]: {
			gridColumn: '2',
			gridRow: '1',
			// Let the heading wrap while leaving room for Respond beside it.
			maxWidth: '24ch',
		},
	},
	descriptionWrapper: {
		gridColumn: '2',
		gridRow: '2',
		minWidth: '0px',
		['@container approval-banner (max-width: 560px)' satisfies ContainerAtRule]: {
			gridColumn: '1',
			gridRow: '3',
		},
	},
	messageDescription: {
		// Leave room for Respond beside a long message in a 760px banner.
		maxWidth: '60ch',
		// At 560px or less, let the message use the full width below the heading.
		['@container approval-banner (max-width: 560px)' satisfies ContainerAtRule]: {
			gridColumn: '1 / -1',
			gridRow: '2',
		},
	},
	progressBarWrapper: {
		// Keep the progress bar at least 120px wide and let it fill extra space.
		minWidth: '120px',
		flex: '1 1 120px',
	},
	avatarsWrapper: {
		flexShrink: 0,
	},
	ctaWrapper: {
		display: 'flex',
		alignItems: 'center',
		// Keeps the dismiss button 4px after the CTA when present.
		gap: token('space.050'),
		minWidth: '0px',
		flexShrink: 0,
	},
});

const avatarStyles = cssMap({
	overflowItem: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingTop: token('space.075'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		cursor: 'default',
	},
	overflowItemName: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	clickableAvatar: {
		cursor: 'pointer',
	},
	clickableOverflowItem: {
		cursor: 'pointer',
	},
});

const overflowProfileCardAnchorStyles = css({
	display: 'block',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Keep the selected profile card anchored while its overflow row scrolls out of view.
	'&:has([aria-expanded="true"])': {
		position: 'sticky',
		top: 0,
		bottom: 0,
		zIndex: 1,
		backgroundColor: token('elevation.surface.overlay'),
	},
});

interface ActionConfig {
	type: 'button';
	label: string;
	onClick: () => void;
}

const ProfileCardAvatar: React.FC<{ avatar: BannerAvatarProps; isClickable: boolean }> = ({
	avatar,
	isClickable,
}) => {
	return (
		<span data-testid="profile-card-avatar" css={[isClickable && avatarStyles.clickableAvatar]}>
			<Avatar {...avatar} />
		</span>
	);
};

interface DismissConfig {
	label: string;
	onClick: () => void;
}

export interface BannerComponentProps {
	heading: string;
	description?: string;
	avatarData?: BannerAvatarProps[];
	action: ActionConfig;
	/** Renders a close (X) button after the action button */
	dismiss?: DismissConfig;
	showProgressBar?: boolean;
	icon?: React.ReactNode;
	profileCardMode?: ProfileCardMode;
}

const AvatarAndCtaRenderer: React.FC<{
	action: ActionConfig;
	dismiss?: DismissConfig;
	avatarData?: BannerAvatarProps[];
	profileCardMode?: ProfileCardMode;
}> = ({ action, dismiss, avatarData, profileCardMode }) => {
	const showAvatars = avatarData != null && avatarData.length > 0;

	const renderAvatar = React.useCallback(
		(avatar: BannerAvatarProps) => {
			if (!avatar.userId) {
				return <Avatar {...avatar} />;
			}

			return (
				<ProfileCardWrapper
					userId={avatar.userId}
					fullName={avatar.name}
					trigger={profileCardMode === 'click' ? 'click' : undefined}
				>
					<ProfileCardAvatar avatar={avatar} isClickable={profileCardMode === 'click'} />
				</ProfileCardWrapper>
			);
		},
		[profileCardMode],
	);

	const renderOverflowItem = React.useCallback<AvatarGroupItemRender>(
		(
			_Component: AvatarGroupItemRenderParams[0],
			itemProps: AvatarGroupItemRenderParams[1],
			index: AvatarGroupItemRenderParams[2],
		) => {
			const avatar = itemProps.avatar as BannerAvatarProps;
			const row = (
				<Box
					xcss={cx(
						avatarStyles.overflowItem,
						Boolean(avatar.userId) && avatarStyles.clickableOverflowItem,
					)}
				>
					<Avatar {...avatar} size="small" />
					<Text size="small">
						<Box as="span" xcss={avatarStyles.overflowItemName}>
							{avatar.name}
						</Box>
					</Text>
				</Box>
			);

			if (!avatar.userId || profileCardMode === undefined) {
				return <React.Fragment key={`overflow-item-${index}`}>{row}</React.Fragment>;
			}

			return (
				<div
					key={`overflow-item-${index}`}
					data-testid="overflow-profile-card-anchor"
					css={overflowProfileCardAnchorStyles}
				>
					<ProfileCardWrapper
						userId={avatar.userId}
						fullName={avatar.name}
						trigger={profileCardMode}
						position="right-start"
						offset={[0, 8]}
						ariaLabel={avatar.name}
						fullWidthTrigger
					>
						{row}
					</ProfileCardWrapper>
				</div>
			);
		},
		[profileCardMode],
	);

	const overrides = React.useMemo<AvatarGroupOverrides>(
		() => ({ AvatarGroupItem: { render: renderOverflowItem } }),
		[renderOverflowItem],
	);

	return (
		<React.Fragment>
			{showAvatars && (
				<div css={styles.avatarsWrapper}>
					<AvatarGroup
						data={avatarData}
						maxCount={2}
						isTooltipDisabled={profileCardMode !== undefined}
						{...(profileCardMode !== undefined ? { avatar: renderAvatar, overrides } : {})}
					/>
				</div>
			)}
			<div css={styles.ctaWrapper}>
				<Button appearance="default" onClick={action.onClick}>
					{action.label}
				</Button>
				{dismiss && (
					<IconButton
						icon={CrossIcon}
						label={dismiss.label}
						appearance="subtle"
						onClick={dismiss.onClick}
					/>
				)}
			</div>
		</React.Fragment>
	);
};

export const BannerComponent: React.FC<BannerComponentProps> = ({
	heading,
	description,
	showProgressBar,
	icon,
	...props
}) => {
	return (
		<div css={styles.container}>
			<div css={styles.wrapper}>
				<div
					css={[
						styles.leftSection,
						!showProgressBar && styles.messageSection,
						expValEquals('confluence_approvals_banner_updates_exp', 'isEnabled', true) &&
							styles.leftSectionSpec,
					]}
				>
					<div
						css={[
							styles.iconWrapper,
							expValEquals('confluence_approvals_banner_updates_exp', 'isEnabled', true) &&
								styles.iconWrapperSpec,
						]}
					>
						{icon ?? (
							<StatusWorkflowInReviewIcon label={heading} color={token('color.icon.accent.blue')} />
						)}
					</div>

					<div css={[styles.headingWrapper, !showProgressBar && styles.messageHeading]}>
						<Heading size="small">{heading}</Heading>
					</div>
					{description && (
						<div css={[styles.descriptionWrapper, !showProgressBar && styles.messageDescription]}>
							<Text size="medium" color="color.text.subtlest">
								{description}
							</Text>
						</div>
					)}
				</div>

				<div css={[styles.supportingContent, !showProgressBar && styles.messageSupportingContent]}>
					{showProgressBar && (
						<div css={styles.progressBarWrapper}>
							<ApprovalsProgressBar />
						</div>
					)}

					<AvatarAndCtaRenderer {...props} />
				</div>
			</div>
		</div>
	);
};
